if (!customElements.get('by-trust-badge-slider')) {
  class ByTrustBadgeSlider extends HTMLElement {
    constructor() {
      super();
      this.viewport = null;
      this.track = null;
      this.slides = [];
      this.prevBtns = [];
      this.nextBtns = [];
      this.progressTrack = null;
      this.progressBar = null;
      this.dotsContainer = null;
      this.dots = [];
      this.autoplayTimer = null;
      this.isDragging = false;
      this.startX = 0;
      this.scrollLeftStart = 0;
      this.dragMoved = false;
      this.rafId = null;

      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onMouseDown = this.onMouseDown.bind(this);
      this.onMouseMove = this.onMouseMove.bind(this);
      this.onMouseUp = this.onMouseUp.bind(this);
      this.onClickCapture = this.onClickCapture.bind(this);
      this.onProgressClick = this.onProgressClick.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);
      this.onBlockDeselect = this.onBlockDeselect.bind(this);
    }

    connectedCallback() {
      this.handlePlacement();
      this.init();
    }

    disconnectedCallback() {
      this.cleanup();
    }

    handlePlacement() {
      const placement = this.dataset.pdpPlacement;
      if (!placement || placement === 'section_position' || this.dataset.teleported === 'true') {
        return;
      }

      const productBlocks = document.querySelector('.product__info-container .product__blocks');
      if (!productBlocks) return;

      this.dataset.teleported = 'true';
      const buyButtonsBlock = productBlocks.querySelector('.product__block--buy_buttons');
      const priceBlock = productBlocks.querySelector('.product__block--price');

      if (placement === 'product_info_below_buttons' && buyButtonsBlock) {
        buyButtonsBlock.insertAdjacentElement('afterend', this);
      } else if (placement === 'product_info_above_buttons' && buyButtonsBlock) {
        buyButtonsBlock.insertAdjacentElement('beforebegin', this);
      } else if (placement === 'product_info_below_price' && priceBlock) {
        priceBlock.insertAdjacentElement('afterend', this);
      } else {
        productBlocks.appendChild(this);
      }
    }

    init() {
      this.cleanup();

      this.viewport = this.querySelector('[data-tbs-viewport]');
      this.track = this.querySelector('[data-tbs-track]');
      if (!this.viewport || !this.track) return;

      this.slides = Array.from(this.track.querySelectorAll('[data-tbs-slide]'));
      this.prevBtns = Array.from(this.querySelectorAll('[data-tbs-prev]'));
      this.nextBtns = Array.from(this.querySelectorAll('[data-tbs-next]'));
      this.progressTrack = this.querySelector('[data-tbs-progress]');
      this.progressBar = this.querySelector('[data-tbs-progress-bar]');
      this.dotsContainer = this.querySelector('[data-tbs-dots]');

      this.loop = this.dataset.loop === 'true';
      this.autoplay = this.dataset.autoplay === 'true';
      this.autoplaySpeed = parseInt(this.dataset.autoplaySpeed || '4000', 10);
      this.pauseOnHover = this.dataset.pauseOnHover !== 'false';
      this.draggable = this.dataset.draggable !== 'false';
      this.slidesToScroll = Math.max(1, parseInt(this.dataset.slidesToScroll || '1', 10));
      this.progressStyle = this.dataset.progressStyle || 'scrollbar';

      this.viewport.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize, { passive: true });

      this.prevBtns.forEach((btn) => {
        btn.addEventListener('click', () => this.step(-1));
      });
      this.nextBtns.forEach((btn) => {
        btn.addEventListener('click', () => this.step(1));
      });

      if (this.progressTrack) {
        this.progressTrack.addEventListener('click', this.onProgressClick);
      }

      if (this.draggable) {
        this.viewport.addEventListener('mousedown', this.onMouseDown);
        window.addEventListener('mousemove', this.onMouseMove);
        window.addEventListener('mouseup', this.onMouseUp);
        this.viewport.addEventListener('click', this.onClickCapture, true);
      }

      if (this.pauseOnHover && this.autoplay) {
        this.addEventListener('mouseenter', () => this.stopAutoplay());
        this.addEventListener('mouseleave', () => this.startAutoplay());
        this.addEventListener('touchstart', () => this.stopAutoplay(), { passive: true });
        this.addEventListener('touchend', () => this.startAutoplay(), { passive: true });
      }

      document.addEventListener('shopify:block:select', this.onBlockSelect);
      document.addEventListener('shopify:block:deselect', this.onBlockDeselect);

      this.buildDots();
      this.updateUI();
      if (this.autoplay) {
        this.startAutoplay();
      }
    }

    cleanup() {
      this.stopAutoplay();
      if (this.rafId) cancelAnimationFrame(this.rafId);
      if (this.viewport) {
        this.viewport.removeEventListener('scroll', this.onScroll);
        this.viewport.removeEventListener('mousedown', this.onMouseDown);
        this.viewport.removeEventListener('click', this.onClickCapture, true);
      }
      if (this.progressTrack) {
        this.progressTrack.removeEventListener('click', this.onProgressClick);
      }
      window.removeEventListener('resize', this.onResize);
      window.removeEventListener('mousemove', this.onMouseMove);
      window.removeEventListener('mouseup', this.onMouseUp);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
    }

    getSlideStepWidth() {
      if (!this.slides.length) return this.viewport ? this.viewport.clientWidth : 0;
      const firstSlide = this.slides[0];
      const style = window.getComputedStyle(this.track);
      const gap = parseFloat(style.columnGap || style.gap || '12') || 0;
      return firstSlide.getBoundingClientRect().width + gap;
    }

    getCurrentIndex() {
      if (!this.viewport || !this.slides.length) return 0;
      const stepWidth = this.getSlideStepWidth();
      if (stepWidth <= 0) return 0;
      const maxScroll = Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth);
      if (maxScroll > 0 && this.viewport.scrollLeft >= maxScroll - 4) {
        return this.slides.length - 1;
      }
      return Math.min(
        this.slides.length - 1,
        Math.max(0, Math.round(this.viewport.scrollLeft / stepWidth))
      );
    }

    scrollToSlide(index) {
      if (!this.viewport || !this.slides.length) return;
      const clamped = Math.max(0, Math.min(this.slides.length - 1, index));
      const targetSlide = this.slides[clamped];
      if (!targetSlide) return;
      const maxScroll = Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth);
      const targetLeft = Math.min(maxScroll, targetSlide.offsetLeft);
      this.viewport.scrollTo({
        left: targetLeft,
        behavior: 'smooth'
      });
    }

    step(direction) {
      if (!this.viewport || !this.slides.length) return;
      const maxScroll = Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth);
      const stepWidth = this.getSlideStepWidth() * this.slidesToScroll;
      const currentLeft = this.viewport.scrollLeft;

      if (direction > 0) {
        if (currentLeft >= maxScroll - 4) {
          if (this.loop) {
            this.viewport.scrollTo({ left: 0, behavior: 'smooth' });
          }
        } else {
          this.viewport.scrollTo({
            left: Math.min(maxScroll, currentLeft + stepWidth),
            behavior: 'smooth'
          });
        }
      } else {
        if (currentLeft <= 4) {
          if (this.loop) {
            this.viewport.scrollTo({ left: maxScroll, behavior: 'smooth' });
          }
        } else {
          this.viewport.scrollTo({
            left: Math.max(0, currentLeft - stepWidth),
            behavior: 'smooth'
          });
        }
      }
    }

    buildDots() {
      if (!this.dotsContainer) return;
      this.dotsContainer.innerHTML = '';
      this.dots = [];

      if (this.slides.length <= 1) {
        this.dotsContainer.style.display = 'none';
        return;
      }

      this.dotsContainer.style.display = '';
      this.slides.forEach((_, idx) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'by-tbs__dot' + (idx === 0 ? ' is-active' : '');
        dot.setAttribute('aria-label', `Go to slide ${idx + 1}`);
        dot.addEventListener('click', () => {
          this.scrollToSlide(idx);
          if (this.autoplay) {
            this.stopAutoplay();
            this.startAutoplay();
          }
        });
        this.dotsContainer.appendChild(dot);
        this.dots.push(dot);
      });
    }

    onScroll() {
      if (this.rafId) cancelAnimationFrame(this.rafId);
      this.rafId = requestAnimationFrame(() => this.updateUI());
    }

    onResize() {
      this.updateUI();
    }

    updateUI() {
      if (!this.viewport) return;
      const scrollLeft = this.viewport.scrollLeft;
      const scrollWidth = this.viewport.scrollWidth;
      const clientWidth = this.viewport.clientWidth;
      const maxScroll = Math.max(0, scrollWidth - clientWidth);
      const ratio = maxScroll > 0 ? Math.min(1, Math.max(0, scrollLeft / maxScroll)) : 1;

      if (this.progressBar) {
        if (this.progressStyle === 'fill') {
          const minPct = this.slides.length > 0 ? Math.max(15, 100 / this.slides.length) : 100;
          const fillPct = maxScroll > 0 ? minPct + ratio * (100 - minPct) : 100;
          this.progressBar.style.width = `${fillPct}%`;
          this.progressBar.style.transform = 'translateX(0)';
        } else {
          const visibleRatio = scrollWidth > 0 ? Math.min(1, Math.max(0.22, clientWidth / scrollWidth)) : 1;
          const thumbWidthPct = visibleRatio * 100;
          const maxTranslatePct = ((100 - thumbWidthPct) / thumbWidthPct) * 100;
          this.progressBar.style.width = `${thumbWidthPct}%`;
          this.progressBar.style.transform = `translateX(${ratio * maxTranslatePct}%)`;
        }
      }

      if (!this.loop) {
        const atStart = scrollLeft <= 4;
        const atEnd = scrollLeft >= maxScroll - 4;
        this.prevBtns.forEach((btn) => {
          btn.disabled = atStart;
        });
        this.nextBtns.forEach((btn) => {
          btn.disabled = atEnd;
        });
      } else {
        this.prevBtns.forEach((btn) => {
          btn.disabled = false;
        });
        this.nextBtns.forEach((btn) => {
          btn.disabled = false;
        });
      }

      if (this.dots.length) {
        const activeIdx = this.getCurrentIndex();
        this.dots.forEach((dot, idx) => {
          dot.classList.toggle('is-active', idx === activeIdx);
        });
      }
    }

    onProgressClick(e) {
      if (!this.viewport || !this.progressTrack) return;
      const rect = this.progressTrack.getBoundingClientRect();
      if (rect.width <= 0) return;
      const clickRatio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
      const maxScroll = Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth);
      this.viewport.scrollTo({
        left: clickRatio * maxScroll,
        behavior: 'smooth'
      });
    }

    onMouseDown(e) {
      if (e.button !== 0) return;
      this.isDragging = true;
      this.dragMoved = false;
      this.startX = e.pageX;
      this.scrollLeftStart = this.viewport.scrollLeft;
      this.viewport.classList.add('is-dragging');
      this.stopAutoplay();
    }

    onMouseMove(e) {
      if (!this.isDragging || !this.viewport) return;
      const dx = e.pageX - this.startX;
      if (Math.abs(dx) > 5) {
        this.dragMoved = true;
      }
      this.viewport.scrollLeft = this.scrollLeftStart - dx;
    }

    onMouseUp() {
      if (!this.isDragging) return;
      this.isDragging = false;
      if (this.viewport) {
        this.viewport.classList.remove('is-dragging');
        if (this.dataset.freeScroll !== 'true' && this.dragMoved) {
          this.scrollToSlide(this.getCurrentIndex());
        }
      }
      if (this.autoplay) {
        this.startAutoplay();
      }
    }

    onClickCapture(e) {
      if (this.dragMoved) {
        e.preventDefault();
        e.stopPropagation();
        this.dragMoved = false;
      }
    }

    startAutoplay() {
      this.stopAutoplay();
      if (!this.autoplay || this.slides.length <= 1) return;
      this.autoplayTimer = setInterval(() => {
        if (!this. viewport) return;
        const maxScroll = Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth);
        if (this.viewport.scrollLeft >= maxScroll - 4) {
          this.viewport.scrollTo({ left: 0, behavior: 'smooth' });
        } else {
          this.step(1);
        }
      }, this.autoplaySpeed);
    }

    stopAutoplay() {
      if (this.autoplayTimer) {
        clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    }

    onBlockSelect(e) {
      const blockId = e.detail && e.detail.blockId;
      if (!blockId) return;
      const slideIdx = this.slides.findIndex((slide) => slide.dataset.blockId === blockId);
      if (slideIdx !== -1) {
        this.stopAutoplay();
        this.scrollToSlide(slideIdx);
      }
    }

    onBlockDeselect(e) {
      const blockId = e.detail && e.detail.blockId;
      if (!blockId) return;
      const slideIdx = this.slides.findIndex((slide) => slide.dataset.blockId === blockId);
      if (slideIdx !== -1 && this.autoplay) {
        this.startAutoplay();
      }
    }
  }

  customElements.define('by-trust-badge-slider', ByTrustBadgeSlider);
}

